import { Loader } from '@googlemaps/js-api-loader';
import config from '@/shared/constants/config';

let googleApiLoading = false;
const googleDeferredPromises = [] as Deferred[];

class Deferred {
  public promise: Promise<any>;
  public resolve: ((value?: any) => void) | undefined;
  public reject: ((value?: any) => void) | undefined;

  constructor() {
    this.promise = new Promise((resolve, reject) => {
      this.resolve = resolve;
      this.reject = reject;
    });
  }
}

export class GoogleMapLoaderService {
  public static load() {
    if(config.IS_PRERENDER) {
      return Promise.resolve();
    }

    if (window.google && window.google.maps) {
      return Promise.resolve();
    }

    if (!googleApiLoading) {
      googleApiLoading = true;

      const loader = new Loader({
        apiKey: config.GOOGLE_MAPS_API_KEY,
        version: config.IS_DATA_DRIVEN_STYLING_ENABLED ? 'beta' : 'quarterly', // '3.38', //weekly
        libraries: ['places'],
        language: config.LANGUAGE as string,
      });

      return new Promise<void>((resolve, reject) => {
        loader
          .loadPromise()
          .then(() => {
            //https://gist.github.com/humaan/a0fcde2813d4722cd7f0
            //https://stackoverflow.com/questions/49434206/adding-custom-markers-htmlmarkers-to-clustering
            const CustomMarkerLabel = class extends google.maps.OverlayView {
              div: null | HTMLElement = null;
              divListeners = [] as { eventName: string; handler: any }[];
              label: any;
              position: any;
              className = '';

              constructor(options: any) {
                super();
                this.setValues(options);
              }

              draw() {
                if (!this.div) {
                  this.div = document.createElement('DIV');
                  for (const listener of this.divListeners) {
                    this.div.addEventListener(listener.eventName, listener.handler);
                  }
                  this.div.className = 'map-marker';
                  if (this.className) {
                    this.div.className += ' ' + this.className;
                  }
                  this.div.style.position = 'absolute';
                  this.div.style.transform = 'translate(-50%,-100%)';
                  this.div.innerHTML = '<span>' + this.label + '</span>';
                  const panes = this.getPanes() as any;
                  if (panes.floatPane) {
                    panes.floatPane.appendChild(this.div);
                  } else if (panes.overlayMouseTarget) {
                    panes.overlayMouseTarget.appendChild(this.div);
                  } else if (panes.overlayImage) {
                    panes.overlayImage.appendChild(this.div);
                  }
                }

                const point = this.getProjection().fromLatLngToDivPixel(this.position);

                if (point) {
                  this.div.style.left = point.x + 'px';
                  this.div.style.top = point.y + 'px';
                }
              }

              addLabelListener(eventName: any, handler: (this: any, event: MouseEvent) => void) {
                this.divListeners.push({ eventName, handler });
              }

              getDraggable() {
                return false;
              }

              getPosition() {
                return this.position;
              }

              onRemove() {
                if (this.div && this.div.parentNode && this.div.parentNode.removeChild) {
                  this.div.parentNode.removeChild(this.div);
                  this.div = null;
                }
              }
            };

            (window.google.maps.Marker.prototype as any).setMapInternal = window.google.maps.Marker.prototype.setMap;

            window.google.maps.Marker.prototype.setMap = function (map) {
              (this as any).setMapInternal(map);
              const markerLabel = (this as any).markerLabel;
              if (markerLabel) {
                markerLabel.setMap(map);
              }
            };
            window.google.maps.Marker.prototype.setLabel = function (label) {
              (this as any).markerLabel = new CustomMarkerLabel({
                map: this.getMap(),
                position: this.getPosition(),
                label: typeof label === 'string' || label === null ? '' : label.text,
                className: typeof label === 'string' || label === null ? '' : label.fontFamily,
              });
            };

            resolve();

            googleDeferredPromises.forEach((x) => {
              if (x.resolve) {
                x.resolve();
              }
            });
          })
          .catch(() => {
            reject();
            googleDeferredPromises.forEach((x) => {
              if (x.reject) {
                x.reject();
              }
            });
          });
      });
    } else {
      const deferred = new Deferred();
      googleDeferredPromises.push(deferred);
      return deferred.promise;
    }
  }
}
